/**
 * Take Over dialog copy, in the chart register (Take Over v2 plan, "The
 * office moment"). Kept in one place so the plan's verbatim lines stay
 * findable, and so tests assert the same strings the dialog renders.
 */

export const TAKE_OVER_TITLE = 'Take over this chart';

// A frozen agency: it has an External EMR, but Roger no longer syncs with it.
// Take Over goes on with the imported records as they are (Daniel, 2026-10-13).
export const FROZEN_LINE =
  'Roger no longer syncs with the External EMR for this agency. The imported records are frozen.';
export const OVERRIDE_INTRO_LINE =
  'Take Over uses them as they are. Give a reason.';
export const OVERRIDE_REASON_LABEL = 'Reason for Take Over';
export const OVERRIDE_CONTINUE_LABEL = 'Continue';

export function frozenReasonLine(reason: string): string {
  return `Frozen records. Reason: ${reason}`;
}
export const REFRESH_RUNNING_LINE =
  'A refresh of this chart is running. Take Over can be confirmed once it finishes.';
export const OVERRIDE_CLEARED_LINE =
  'Roger can read the External EMR for this agency again, so the override no longer applies.';
export const PLAN_RELOAD_FAILED_LINE =
  'The plan could not be reloaded. Try again before you take over.';
export const PLAN_CHANGED_LINE =
  'The chart changed since this plan was shown. Review the plan again.';
// The Patient Manager's Refresh says the same while the backfill runs.
export const HISTORY_ARRIVING_LINE =
  'Earlier episodes are still arriving. Take Over when they land.';
// On the access-lost override with the backfill still queued: Take Over
// cancels it, so it cannot land after the move.
export const HISTORY_MISSING_OVERRIDE_LINE =
  'Earlier episodes have not all arrived. Take Over cancels that import and moves the chart with what Roger has.';

// After the agency's last day in the External EMR, a failed read no longer
// stops Take Over (Daniel, 2026-10-15). No reason is typed.
export function refreshWaivedLine(lastDay: string): string {
  return `The External EMR could not be read. Its last day was ${lastDay}; Roger takes over on the records it holds.`;
}

// Step 2: the plan.
export function rogerBillsFromSentence(date: string | null): string {
  return date
    ? `Roger bills from ${date}.`
    : 'Roger bills no claim on this period.';
}

export function rogerChartsFromSentence(date: string): string {
  return `Roger charts starting from ${date}.`;
}

export const CHARTS_FROM_LABEL = 'Roger charts from';
// A day before today: imported visits from it on are managed in Roger. Who
// bills them does not follow the day (Daniel, 2026-10-19). The count is
// returned by the confirm, not shown here (kept simple).
export const CHARTS_FROM_EARLIER_LINE =
  'Visits dated on or after this day are managed in Roger. Who bills each one follows its claim.';

// After a plan reload, a picked day the new plan no longer allows.
export function chartsFromResetLine(date: string): string {
  return `The day you chose no longer fits this plan. Roger charts from ${date} instead.`;
}

// The day Roger charts from falls inside a kept claim: warned, never blocked
// (Daniel, 2026-10-19). The dialog and the census row share these lines and
// each adds its own last line. `numbers` runs from the claim the day falls
// inside through every kept claim after it.
function claimsRun(numbers: number[]): string {
  return numbers.length === 1
    ? `claim ${numbers[0]}`
    : `claims ${numbers[0]}–${numbers[numbers.length - 1]}`;
}

export function chartsInsideKeptLine(
  day: string,
  numbers: number[],
  start: string,
  end: string
): string {
  const those = numbers.length === 1 ? 'that claim' : 'those claims';
  return (
    `Roger charts from ${day}, inside ${claimsRun(numbers)} (${start} – ${end}). ` +
    `The External EMR keeps ${those}. It will not have the visits charted in Roger.`
  );
}

// Census row, after the move: the EXTERNAL claim is written, and the External
// EMR may have billed it. Nothing moves it now.
export function keptClaimsStayLine(count: number): string {
  return count === 1
    ? 'The claim stays with the External EMR.'
    : 'The claims stay with the External EMR.';
}

export function externalKeepsSentence(periods: string[]): string {
  if (periods.length === 0) return 'The External EMR keeps no claims.';
  const noun = periods.length === 1 ? 'claim' : 'claims';
  return `The External EMR keeps ${periods.length} ${noun}: ${periods.join(', ')}.`;
}

export const NOA_EXTERNAL_FILED = 'The External EMR filed it';
export const NOA_ROGER_SENT = 'Roger already sent it';
export const NOA_NOT_NEEDED = 'Not needed for this payer';

export function noaRogerSends(due: string | null): string {
  return due ? `Roger will send it, due ${due}` : 'Roger will send it';
}

export function noaSentence(choice: string): string {
  return `Notice of Admission: ${choice}.`;
}

// A notice still in Roger's queue after its last on-time day.
export function noaOverdueLine(due: string | null): string {
  return due
    ? `This notice was due ${due}. Send it now, or confirm the External EMR filed it.`
    : 'This notice is late. Send it now, or confirm the External EMR filed it.';
}

export const NOA_ACTION_LINE =
  'Confirm the External EMR sent the Notice of Admission.';
export const NOA_WARNING_LINE =
  'The External EMR keeps claim 1, and Medicare accepts that claim only with the Notice of Admission on file. Send Roger’s notice before the External EMR sends claim 1.';

// A parked notice recorded for another payer: unchecked keeps that payer.
export function noaPayerConfirmLabel(
  current: string,
  recorded: string
): string {
  return `The External EMR's notice counts for ${current} (it is recorded for ${recorded})`;
}

export function unfinishedVisitLine(visit: string): string {
  return `${visit} is saved but not finished in the External EMR. Finish it there.`;
}

export function editedRowsLine(count: number): string {
  const rows =
    count === 1
      ? '1 imported visit or task was'
      : `${count} imported visits or tasks were`;
  return `${rows} changed in Roger. Later changes in the External EMR do not overwrite them.`;
}

// Step 2: confirm. Roger keeps reading while the External EMR stays on; at a
// frozen agency it reads nothing more, so it promises no read.
export function confirmLine(date: string, syncing: boolean): string {
  const reads = syncing
    ? 'Roger keeps reading the External EMR for this chart while the External EMR stays on.'
    : 'Roger no longer reads the External EMR for this agency.';
  return `From ${date}, chart only in Roger. ${reads}`;
}

export function untilChartsFromLine(date: string): string {
  return `Until ${date}, keep charting in the External EMR.`;
}
export const CHECKLIST_HEADING = 'Before you leave the External EMR';
// PLACEHOLDER pending the Q4 read-only probe of how the External EMR shows
// an unassigned visit (Take Over rulings, 2026-10-12). Roger never writes to
// the External EMR; this line is advice only.
export const CHECKLIST_REMAINING_VISITS =
  'In the External EMR: check the remaining visits on this period.';

export function checklistDoNotSend(numbers: number[]): string {
  if (numbers.length === 1) {
    return `Do not send claim ${numbers[0]} from the External EMR; Roger bills it.`;
  }
  const first = numbers[0];
  const last = numbers[numbers.length - 1];
  return `Do not send claims ${first}–${last} from the External EMR; Roger bills them.`;
}

export const CHECKLIST_SYNC_REVIEW =
  "Items the External EMR's claim still needs appear on this chart's sync review.";
export const CHECKLIST_SYNC_REVIEW_FROZEN =
  'Roger no longer syncs, so check the External EMR for what its claims still need.';

// After the move, on the chart.
export function chartMovedLine(date: string, chartsFrom?: string): string {
  return chartsFrom && chartsFrom !== date
    ? `This chart moved to Roger on ${date}. Roger charts it from ${chartsFrom}.`
    : `This chart moved to Roger on ${date}. Chart all new work in Roger.`;
}
// A move by Add Episode or an intake leaves the imported period's billing
// where it was (Daniel, 2026-10-13: "3) OK.").
export const CHART_MOVED_EXTERNAL_BILLS_LINE =
  'The External EMR keeps billing the period it built.';

// Undo Take Over (Take Over v2 WP-U2). Daniel, 2026-10-13: "a narrow-scope
// undo now, so long as no work or claim has been done in Roger."
export const UNDO_BUTTON = 'Undo';
export const UNDO_TITLE = 'Undo Take Over';
export const UNDO_BACK_LINE = 'This chart goes back to the External EMR.';

export function undoCyclesLine(count: number): string {
  const removes =
    count === 0
      ? 'Roger created no claim periods on this chart.'
      : count === 1
        ? 'Roger removes 1 claim period it created.'
        : `Roger removes ${count} claim periods it created.`;
  return `${removes} Roger has no work on this chart to keep.`;
}

// One claim period undo removes, as its dates.
export function undoCycleRange(start: string, end: string): string {
  return `${start} to ${end}`;
}

export function undoNoaLine(restored: string): string {
  return `Notice of Admission goes back to ${restored}.`;
}

export const UNDO_HAZARD_LINE =
  'A visit started in the app after the Take Over cannot be sent after this undo.';
export const UNDO_REASON_LABEL = 'Why this Take Over is undone';
export const UNDO_CONFIRM = 'Undo Take Over';
export const UNDO_BLOCKED_TITLE = 'Take Over cannot be undone.';
export const UNDO_NOT_TAKE_OVER_LINE = 'This chart was not moved by Take Over.';
export const UNDO_LOAD_FAILED_LINE =
  'Unable to check this Take Over. Please try again.';
export const UNDO_FAILED_TOAST = 'Failed to undo Take Over.';
export const UNDO_SUCCESS_TOAST =
  'Take Over undone. Chart this patient in the External EMR until it is taken over again.';

// On the episode header, once a Take Over of this period was undone.
export function takeOverUndoneLine(
  date: string,
  name: string | null | undefined,
  reason: string
): string {
  const by = name ? ` by ${name}` : '';
  return `Take Over undone ${date}${by}: ${reason}`;
}

// What the External EMR's claim history says about one period.
export const EXTERNAL_CLAIM_READING_LINE = {
  sent: 'Sent from the External EMR.',
  paid: 'Paid in the External EMR.',
  correcting: 'Being corrected in the External EMR.',
  undone: 'Voided in the External EMR.',
} as const;
export const EXTERNAL_CLAIM_LINES_TITLE =
  'From the External EMR claims history';
